import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { DELETE_DOMAIN_PERMISSION_MESSAGE, DESCRIPTION, DOMAIN_NAME, NO_OF_CHANNEL, UPDATE_DOMAIN_PERMISSION_MESSAGE } from '../Utils/Constants';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import ConfirmDialog from '../Components/ConfirmDialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { TIndustry } from '../Types/domain.types';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { AppContext } from 'Context/AppContext';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type DomainTableProps = {
    partyId: string;
    searchTerm: string;
    showLoader: boolean;
    domains: TIndustry[]
    setDomains: React.Dispatch<React.SetStateAction<TIndustry[]>>
    setShowLoader: (value: boolean) => void;
    setTotalRecords: (value: number) => void;
    paginationParams: { pageNumber: number; pageSize: number };
    refreshTrigger?: number;
    onDomainsLoaded?: () => void;
    onEditDomain?: (domain: TIndustry, index: number) => void;
    onNavigateToChannel?: (domain: TIndustry) => void;
    deletePermission: any;
    updatePermission: any;
}

/**
 * The domain grid on the design-system `Table` (§ 119) — the § 118 recipe applied to
 * the second Admin list.
 *
 * **The inverted data flow is deliberately unchanged.** This child owns the fetch and
 * the sort state, then pushes `domains`, `showLoader` and `totalRecords` UP into
 * `ManageDomain` through setter props, while the parent owns pagination and the
 * create-first empty-state gate. That is the shape the 2026-08-11 bug came out of
 * (`okf/admin/e2e.md` § 5.2) and it is worth untangling — but it is a state-ownership
 * change, not a presentational one, so it is out of scope here and recorded as owed.
 * Note the consequence for tests: this component **cannot** be rendered without a
 * parent holding its data, and its sort state is invisible from outside.
 *
 * Same four legacy shells replaced as in § 118 — `Common/components/TableCell` (whose
 * hardcoded `components-tablecell-td-1` put four columns × N rows behind one testid),
 * the ISR `DropdownMenu`, the ISR `ConfirmationDialog`, and the FontAwesome CDN sort
 * glyphs. All four still serve the Channel and Environment screens.
 *
 * Two things specific to this table:
 *
 * - **Sort is INTERNAL here**, unlike `PartyTable` where the parent owns it. The DS
 *   header's asc→desc→null cycle is still bypassed: `handleColumnClick` flips two-state
 *   and the suggested direction is ignored. The three sortable column ids are the
 *   backend's `sort_by` values (`name`, `description`, `parent_channel_count`).
 * - **`onDomainDeleted` is gone from the props.** It was declared and destructured and
 *   **never called**, and no caller ever passed it — the same defect class as the DS
 *   `Button.badgeVariant` that sat unread for months. Deleting a domain refetches
 *   through this component's own `fetchDomains()`; nothing regressed because nothing
 *   was wired.
 */
const DomainTable = ({ partyId, searchTerm, showLoader, domains, setDomains, setShowLoader, setTotalRecords, paginationParams, refreshTrigger, onDomainsLoaded, onEditDomain, onNavigateToChannel, updatePermission, deletePermission }: DomainTableProps) => {
    const appContext = useContext(AppContext);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        domainIndex: number | null;
        domainName: string;
        domainId: string | null;
    }>({
        isOpen: false,
        domainIndex: null,
        domainName: '',
        domainId: null
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    useEffect(() => {
        if (partyId) {
            fetchDomains();
        }
    }, [partyId, paginationParams, searchTerm, sortBy, sortOrder, refreshTrigger]);

    useEffect(() => {
        /* A fixed-position menu detaches from its row the moment anything scrolls. */
        function handleScroll() {
            setOpenMenuFor(null);
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const fetchDomains = async () => {
        setShowLoader(true);
        try {
            const domainResponse = await ServiceWrapper.doGet( 
                `${api_base_url}party/${partyId}/industry?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            ); 
            const response = domainResponse?.data;
            if (response && response.data) {
                setDomains(response.data);
                setTotalRecords(response.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
            toast.error('Failed to fetch domains. Please try again.');
        } finally {
            setShowLoader(false);
            if (onDomainsLoaded) {
                onDomainsLoaded();
            }
        }
    };

    const handleColumnClick = (column: string) => {
        if (!showLoader) {
            if (sortBy === column) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            } else {
                setSortBy(column);
                setSortOrder('asc');
            }
        }
    };

    const handleEdit = (domain: TIndustry, index: number) => {
        if (!updatePermission) {
            toast.warning(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }

        if (onEditDomain) {
            onEditDomain(domain, index);
        }
    };

    const handleDomainClick = (domain: TIndustry) => {
        if (onNavigateToChannel) {
            onNavigateToChannel(domain);
        }
    };

    const handleDeleteClick = (domain: TIndustry, index: number) => {
        if (!deletePermission) {
            toast.warning(DELETE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }  
        setDeleteConfirmation({
            isOpen: true,
            domainIndex: index,
            domainName: domain.name,
            domainId: domain.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.domainId) return;
        setIsDeleting(true);
        try {            
            const deleteResponse = await ServiceWrapper.doGet( 
                `${api_base_url}industry/delete/${deleteConfirmation.domainId}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success(response.message || 'Domain deleted successfully!');
                fetchDomains();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete domain');
                /* Kept verbatim. It is a no-op — nothing follows in the `try`, and
                   `finally` closes the dialog either way — but removing it would be an
                   unrelated edit in a migration commit. */
                return;
            }
            
        } catch (error) {
            console.error('Error deleting domain:', error);
            toast.error('Failed to delete domain. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                domainIndex: null,
                domainName: '',
                domainId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            domainIndex: null,
            domainName: '',
            domainId: null
        });
    };

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, domainId: string) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setOpenMenuFor(prev => (prev === domainId ? null : domainId));
    };

    const getMenuItems = (domain: TIndustry, index: number): MenuItem[] => [
        {
            id: `edit-${index}`,
            label: 'Edit',
            icon: <EditIcon />,
            'data-testid': `domain-table-menu-edit-${domain.id}`,
            onClick: () => { setOpenMenuFor(null); handleEdit(domain, index); },
        },
        /* A standalone divider entry, NOT `divider: true` on Delete — the DS `Menu`
           renders the rule INSTEAD of the item it is flagged on, where the legacy
           `DropdownMenu` drew the rule and then the item. See § 118: porting the flag
           verbatim deletes the action, with a clean typecheck. */
        { id: `divider-before-delete-${index}`, divider: true },
        {
            id: `delete-${index}`,
            label: 'Delete',
            icon: <DeleteIcon />,
            'data-testid': `domain-table-menu-delete-${domain.id}`,
            onClick: () => { setOpenMenuFor(null); handleDeleteClick(domain, index); },
        },
    ];

    const renderRowActions = (domain: TIndustry, rowIndex: number) => (
        <>
            <button
                type="button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={openMenuFor === domain.id}
                /* Legacy `id` kept verbatim, index and all — specs key off `id` here as
                   often as `data-testid`. The testid moves to the domain's own id;
                   `domain-domaintable-button-1-${index}` renamed rows on every sort. */
                id={`overflow-btn-${rowIndex}`}
                data-testid={`domain-table-overflow-${domain.id}`}
                className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                onClick={(e) => handleOverflowClick(e, domain.id)}
            >
                <OverFlowIcon />
            </button>
            {openMenuFor === domain.id ? ReactDOM.createPortal(
                <div
                    className="tw-fixed tw-z-[9999] tw-w-48"
                    style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                >
                    <Menu
                        variant="compact"
                        items={getMenuItems(domain, rowIndex)}
                        data-testid={`domain-table-menu-${domain.id}`}
                        onDismiss={() => setOpenMenuFor(null)}
                    />
                </div>,
                document.body
            ) : null}
        </>
    );

    const columns: TableColumn<TIndustry>[] = [
        {
            id: 'name',
            header: DOMAIN_NAME,
            accessor: 'name',
            width: '32%',
            sortable: true,
            render: (_value, domain, rowIndex) => (
                <span
                    role="button"
                    tabIndex={0}
                    id={`domain-name-${rowIndex}`}
                    data-testid={`domain-table-name-${domain.id}`}
                    className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer"
                    onClick={() => handleDomainClick(domain)}
                    onKeyDown={(e) => {
                        /* The legacy cell was click-only: the one route into channel
                           management could not be reached from the keyboard. */
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDomainClick(domain);
                        }
                    }}
                >
                    {domain.name}
                </span>
            ),
        },
        {
            id: 'description',
            header: DESCRIPTION,
            accessor: (domain: TIndustry) => domain.description || '-',
            width: '32%',
            sortable: true,
            render: (value, domain, rowIndex) => (
                <span id={`description-${rowIndex}`} data-testid={`domain-table-description-${domain.id}`}>{value}</span>
            ),
        },
        {
            id: 'parent_channel_count',
            header: NO_OF_CHANNEL,
            accessor: 'parent_channel_count',
            width: '31%',
            sortable: true,
            cellType: 'numerical',
            render: (value, domain, rowIndex) => (
                <span id={`no-of-channels-${rowIndex}`} data-testid={`domain-table-channels-${domain.id}`}>{value}</span>
            ),
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '5%',
            align: 'right',
            render: (_value, domain, rowIndex) => renderRowActions(domain, rowIndex),
        },
    ];

    return (
        <>
            <div className="tw-mt-3">
                <Table<TIndustry>
                    /* Bound the grid so <main> stops scrolling the page — see
                       `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                       needs it: sticky resolves against a scrolling ancestor. */
                    stickyHeader
                    maxHeight={PAGE_GRID_MAX_HEIGHT}
                    columns={columns}
                    data={domains ?? []}
                    rowKey="id"
                    striped
                    loading={showLoader}
                    sortState={{ columnId: sortBy, direction: sortOrder === 'asc' ? 'asc' : 'desc' }}
                    onSort={(columnId) => handleColumnClick(columnId)}
                    emptyContent={
                        /* No testid: `Table` already applies `${testid}-empty` to the
                           `<td>` wrapping this. */
                        <Typography variant="body-primary" className="tw-text-ink-secondary">
                            No domains found
                        </Typography>
                    }
                    id="domain-table"
                    data-testid="domain-table"
                />
            </div>

            <ConfirmDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Domain"
                message={`Are you sure you want to delete the domain "${deleteConfirmation.domainName}"?`}
                secondaryMessage="This action cannot be undone."
                tone="danger"
                isLoading={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                data-testid="domain-delete-confirm"
            />
        </>
    );
}

export default DomainTable;
